import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, NavLink, Navigate, useParams, Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { User } from './interfaces/User';
import { Login } from './components/Login';
import { ForgotPassword } from './components/ForgotPassword';
import { ResetPassword } from './components/ResetPassword';
import { ExternalSignup } from './components/ExternalSignup';
import { ExternalLogin } from './components/ExternalLogin';
import { ManageProducts } from './components/ManageProducts';
import { Account } from './components/Account';
import { ProductsPage } from './pages/ProductsPage';
import { OrdersPage } from './pages/OrdersPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { TermsPage } from './pages/TermsPage';
import { FaqPage } from './pages/FaqPage';
import { GuaranteePage } from './pages/GuaranteePage';
import { OrderDashboardPage } from './pages/OrderDashboardPage';
import { EarningsPage } from './pages/EarningsPage';
import { UsersPage } from './pages/UsersPage';
import { SearchLogsPage } from './pages/SearchLogsPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { AccountPayoutsReturnPage } from './pages/AccountPayoutsReturnPage';
import { AccountPayoutsRefreshPage } from './pages/AccountPayoutsRefreshPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { CategoryPage } from './pages/CategoryPage';
import { UserPage } from './pages/UserPage';
import { useTranslation, SUPPORTED as SUPPORTED_LANGS } from './i18n';
import { accountTabForMessage, isAccountActionMessage } from './accountAction';
import { AccountActionAlert } from './components/AccountActionAlert';
import api, { setLogoutHandler, getAccessToken, setTokens, clearTokens, getRefreshTokenValue } from './api';
import { SiteLogo } from './components/SiteLogo';
import { GlobeIcon } from './components/GlobeIcon';
import { Toast } from './components/Toast';
import { ReportContentButton } from './components/ReportContentButton';
import { consumeFlashMessage } from './utils/flashMessage';
import {
  OfferEligibility,
  accountTabForOfferIssues,
  formatOfferEligibilityBannerMessage,
  shouldShowOfferEligibilityBanner,
} from './offerEligibility';

function RentalRedirect() {
  const { id } = useParams();
  return <Navigate to={`/orders/${id}`} replace />;
}

function LegacyExternalRedirect({ mode }: { mode: 'signup' | 'login' }) {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const provider = searchParams.get('provider') ?? 'mojeid';
  if (!code) {
    return <Navigate to="/login" replace />;
  }
  const params = new URLSearchParams({ provider, code });
  return <Navigate to={`/external-${mode}?${params.toString()}`} replace />;
}

function LoginRoute({ token, children }: { token: string | null; children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  if (token) {
    const redirect = searchParams.get('redirect');
    return <Navigate to={redirect && redirect.startsWith('/') ? redirect : '/'} replace />;
  }
  return <>{children}</>;
}

function App() {
  const { t, lang, setLang } = useTranslation();
  const [token, setToken] = useState<string | null>(getAccessToken());
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [offerEligibility, setOfferEligibility] = useState<OfferEligibility | null>(null);
  const [flashMessage, setFlashMessage] = useState<string | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const navInnerRef = useRef<HTMLDivElement>(null);
  const headerActionsRef = useRef<HTMLDivElement>(null);
  const [headerStacked, setHeaderStacked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleBannerRent = useCallback(() => {
    navigate('/');
    setTimeout(() => {
      const main = document.querySelector('.main-content');
      if (main) {
        main.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, [navigate]);

  const handleBannerOffer = useCallback(() => {
    if (token) {
      navigate('/manage?new=1');
    } else {
      navigate('/login?redirect=' + encodeURIComponent('/manage?new=1'));
    }
  }, [navigate, token]);

  useEffect(() => {
    if (token) {
      const d = jwtDecode<any>(token);
      setUser({
        id: parseInt(d['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']),
        name: d['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
        isAdmin: d['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Admin',
        isVerified: d['isVerified'] === 'true',
      });
    } else {
      setUser(null);
    }
  }, [token]);

  const handleLogin = useCallback((accessToken: string, refreshToken: string) => {
    setTokens(accessToken, refreshToken);
    setToken(accessToken);
  }, []);

  const refreshOfferEligibility = useCallback(async () => {
    if (!getAccessToken()) {
      setOfferEligibility(null);
      return;
    }
    try {
      const res = await api.get<OfferEligibility>('/users/offer-eligibility');
      setOfferEligibility(res.data);
    } catch {
      setOfferEligibility(null);
    }
  }, []);

  useEffect(() => {
    refreshOfferEligibility();
  }, [token, refreshOfferEligibility]);

  useEffect(() => {
    if (location.pathname === '/account' || location.pathname === '/manage') {
      refreshOfferEligibility();
    }
  }, [location.pathname, refreshOfferEligibility]);

  // Flash messages queued before navigation surface on the next page.
  useEffect(() => {
    const queued = consumeFlashMessage();
    if (queued) setFlashMessage(queued);
  }, [location.key]);

  const dismissFlashMessage = useCallback(() => setFlashMessage(null), []);

  const logout = useCallback(async () => {
    const rt = getRefreshTokenValue();
    if (rt) {
      try { await api.post('/auth/revoke', { refreshToken: rt }); } catch { /* ignore */ }
    }
    clearTokens();
    setToken(null);
    setUser(null);
    setOfferEligibility(null);
  }, []);

  useEffect(() => {
    setLogoutHandler(() => {
      clearTokens();
      setToken(null);
      setUser(null);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const update = () => {
      const header = headerRef.current;
      if (header) {
        document.documentElement.style.setProperty(
          '--global-error-top',
          `${header.getBoundingClientRect().bottom}px`
        );
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [isHomePage, token, headerStacked]);

  useEffect(() => {
    const header = headerRef.current;
    const navInner = navInnerRef.current;
    const actions = headerActionsRef.current;
    if (!header || !navInner || !actions) return;

    const logoLink = header.querySelector('a');
    if (!logoLink) return;

    const updateStacked = () => {
      const style = getComputedStyle(header);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      const padding =
        (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const available = header.clientWidth - padding;
      const needed =
        logoLink.getBoundingClientRect().width +
        gap +
        navInner.scrollWidth +
        gap +
        actions.getBoundingClientRect().width;
      setHeaderStacked(needed > available);
    };

    updateStacked();

    const observer = new ResizeObserver(updateStacked);
    observer.observe(header);
    observer.observe(navInner);
    observer.observe(actions);
    observer.observe(logoLink);

    return () => observer.disconnect();
  }, [token, user?.isAdmin, user?.name, lang]);

  return (
    <div className="site-layout">
      <header className="site-header">
        <div
          className={`header-top${headerStacked ? ' header-top--stacked' : ''}`}
          ref={headerRef}
        >
          <Link to="/">
            <SiteLogo className="header-logo" />
          </Link>
          <nav>
            <div className="nav-inner" ref={navInnerRef}>
              <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setError('')}>
                {t('nav.products')}
              </NavLink>
              <NavLink
                to="/categories"
                className={location.pathname.startsWith('/categories') ? 'active' : ''}
                onClick={() => setError('')}
              >
                {t('nav.categories')}
              </NavLink>
              {token && (
                <>
                  <NavLink to="/orders" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setError('')}>
                    {t('nav.orders')}
                  </NavLink>
                  <NavLink to="/earnings" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setError('')}>
                    {t('nav.earnings')}
                  </NavLink>
                  {user?.isAdmin && (
                    <>
                      <NavLink to="/users" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setError('')}>
                        {t('nav.users')}
                      </NavLink>
                      <NavLink to="/search-logs" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setError('')}>
                        {t('nav.search_logs')}
                      </NavLink>
                    </>
                  )}
                  <NavLink to="/manage" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setError('')}>
                    {t('nav.manage')}
                  </NavLink>
                </>
              )}
            </div>
          </nav>
          <div className="header-actions" ref={headerActionsRef}>
            <div className="lang-menu" ref={langRef}>
              <button
                type="button"
                className="lang-toggle"
                onClick={() => setLangOpen(o => !o)}
                aria-label={t('lang.select')}
                aria-haspopup="menu"
                aria-expanded={langOpen}
              >
                <GlobeIcon />
              </button>
              {langOpen && (
                <div className="lang-dropdown" role="menu">
                  {SUPPORTED_LANGS.map(l => (
                    <button
                      key={l}
                      type="button"
                      role="menuitemradio"
                      aria-checked={lang === l}
                      className={`dropdown-link${lang === l ? ' active' : ''}`}
                      onClick={() => { setLang(l); setLangOpen(false); }}
                    >
                      {t(`lang.${l}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {user ? (
              <div className="profile-menu" ref={profileRef}>
                <button className="profile-toggle" onClick={() => setProfileOpen(o => !o)}>
                  <span className="profile-avatar">{user.name.charAt(0).toUpperCase()}</span>
                  <span className="profile-name">{user.name}</span>
                </button>
                {profileOpen && (
                  <div className="profile-dropdown">
                    <div className="profile-dropdown-header">{user.name}</div>
                    <NavLink to="/account" className="dropdown-link" onClick={() => { setProfileOpen(false); setError(''); }}>{t('nav.account')}</NavLink>
                    <hr />
                    <button className="dropdown-logout" onClick={logout}>{t('nav.logout')}</button>
                  </div>
                )}
              </div>
            ) : (
              <NavLink to="/login" className="btn-header-login">{t('nav.signin')}</NavLink>
            )}
          </div>
        </div>
        {isHomePage && (
          <div className="header-banner">
            <div className="header-banner-content">
              <div className="header-banner-text">{t('app.banner')}</div>
              <div className="header-banner-buttons">
                <button type="button" className="btn-banner btn-banner-rent" onClick={handleBannerRent}>
                  {t('banner.button_rent')}
                </button>
                <button type="button" className="btn-banner btn-banner-offer" onClick={handleBannerOffer}>
                  {t('banner.button_offer')}
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {error && (
        <div className="global-error-banner" role="alert">
          <div className="global-error-banner__inner">
            {isAccountActionMessage(error, t) ? (
              <AccountActionAlert
                message={error}
                variant="error"
                tab={accountTabForMessage(error, t)}
                onDismiss={() => setError('')}
              />
            ) : (
              <div className="alert alert-error">
                {error}
                <button className="dismiss" onClick={() => setError('')}>&times;</button>
              </div>
            )}
          </div>
        </div>
      )}

      {flashMessage && <Toast message={flashMessage} onDismiss={dismissFlashMessage} />}

      <main className="main-content">
        {shouldShowOfferEligibilityBanner(offerEligibility) && offerEligibility && (
          <div style={{ marginBottom: '1rem' }}>
            <AccountActionAlert
              message={formatOfferEligibilityBannerMessage(offerEligibility.issues, t)}
              variant="warning"
              tab={accountTabForOfferIssues(offerEligibility.issues)}
            />
          </div>
        )}

        <Routes>
          <Route path="/" element={<ProductsPage token={token} userId={user?.id ?? null} isVerified={user?.isVerified ?? false} onLogin={handleLogin} setError={setError} />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/guarantee" element={<GuaranteePage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/categories/:categorySlug" element={
            <CategoryPage
              token={token}
              userId={user?.id ?? null}
              isVerified={user?.isVerified ?? false}
              onLogin={handleLogin}
              setError={setError}
            />
          } />
          <Route path="/user/:id" element={
            <UserPage
              token={token}
              userId={user?.id ?? null}
              isVerified={user?.isVerified ?? false}
              onLogin={handleLogin}
              setError={setError}
            />
          } />
          <Route path="/login" element={<LoginRoute token={token}><Login onLogin={handleLogin} setError={setError} /></LoginRoute>} />
          <Route path="/forgot-password" element={token ? <Navigate to="/" replace /> : <ForgotPassword />} />
          <Route path="/reset-password" element={token ? <Navigate to="/" replace /> : <ResetPassword />} />
          <Route path="/external-signup" element={token ? <Navigate to="/" replace /> : <ExternalSignup onLogin={handleLogin} />} />
          <Route path="/external-login" element={token ? <Navigate to="/" replace /> : <ExternalLogin onLogin={handleLogin} />} />
          <Route path="/mojeid-register" element={<LegacyExternalRedirect mode="signup" />} />
          <Route path="/mojeid-login" element={<LegacyExternalRedirect mode="login" />} />
          {token ? (
            <>
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:id" element={<OrderDashboardPage />} />
              <Route path="/earnings" element={<EarningsPage />} />
              {user?.isAdmin && <Route path="/users" element={<UsersPage currentUserId={user.id} />} />}
              {user?.isAdmin && <Route path="/search-logs" element={<SearchLogsPage />} />}
              <Route path="/manage" element={<ManageProducts user={user} setError={setError} onOfferEligibilityChange={refreshOfferEligibility} />} />
              <Route path="/account" element={<Account user={user} setError={setError} onSessionRefresh={handleLogin} onOfferEligibilityChange={refreshOfferEligibility} />} />
              <Route path="/account/payouts/return" element={<AccountPayoutsReturnPage />} />
              <Route path="/account/payouts/refresh" element={<AccountPayoutsRefreshPage />} />
              <Route path="/rental/orders/:id" element={<RentalRedirect />} />
            </>
          ) : null}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="site-footer">
        <div className="footer-main">
          <div className="footer-inner">
            <div className="footer-company">
              <strong>Rondoshare</strong>
              <span>Ing. Martin Španěl</span>
              <span>{t('footer.ico')}: 23184418</span>
              <span>Trojská 110/23, 182 00 Praha</span>
              <span className="footer-registry">{t('footer.registry')}</span>
              <span className="footer-registry">{t('footer.vat')}</span>
              <a href="tel:+420721674306">+420 721 674 306</a>
              <a href="mailto:info@rondoshare.com">info@rondoshare.com</a>
            </div>
            <div className="footer-center">
              <Link to="/">
                <SiteLogo className="footer-logo" />
              </Link>
              <div className="footer-links">
                <Link to="/terms">{t('footer.terms')}</Link>
                <span className="footer-link-sep">|</span>
                <Link to="/how-it-works">{t('footer.how_it_works')}</Link>
                <span className="footer-link-sep">|</span>
                <Link to="/faq">{t('footer.faq')}</Link>
                <span className="footer-link-sep">|</span>
                <Link to="/guarantee">{lang === 'cs' ? 'Garance' : 'Guarantee'}</Link>
                <span className="footer-link-sep">|</span>
                <ReportContentButton />
              </div>
            </div>
            <div className="footer-social">
              <div className="footer-social-icons">
                <a href="https://www.facebook.com/profile.php?id=61589109270617" aria-label="Facebook" className="social-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
                </a>
                <a href="https://www.instagram.com/rondoshare/" aria-label="Instagram" className="social-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.43.403a4.088 4.088 0 011.47.957c.453.453.774.898.957 1.47.163.46.349 1.26.404 2.43.058 1.265.069 1.645.069 4.849s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.403 2.43a4.088 4.088 0 01-.957 1.47 4.088 4.088 0 01-1.47.957c-.46.163-1.26.349-2.43.404-1.265.058-1.645.069-4.849.069s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.43-.403a4.088 4.088 0 01-1.47-.957 4.088 4.088 0 01-.957-1.47c-.163-.46-.349-1.26-.404-2.43C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.403-2.43a4.088 4.088 0 01.957-1.47A4.088 4.088 0 015.063 2.293c.46-.163 1.26-.349 2.43-.404C8.758 1.831 9.138 1.82 12 1.82V2.163zM12 6.865a5.135 5.135 0 100 10.27 5.135 5.135 0 000-10.27zm0 8.468a3.333 3.333 0 110-6.666 3.333 3.333 0 010 6.666zm5.338-8.671a1.2 1.2 0 11-2.4 0 1.2 1.2 0 012.4 0z"/></svg>
                </a>
              </div>
              <span className="footer-follow-label">{t('footer.follow_us')}</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-bottom-lower">
            © {new Date().getFullYear()} {t('footer.copyright')}
            {' · '}{t('footer.supervision')}{' '}
            <a href="https://www.coi.cz" target="_blank" rel="noopener noreferrer">www.coi.cz</a>
            {' · '}{t('footer.adr')}{' '}
            <a href="https://adr.coi.cz" target="_blank" rel="noopener noreferrer">adr.coi.cz</a>
            {process.env.REACT_APP_COMMIT_HASH && (
              <span className="footer-commit" title="Build version"> · {process.env.REACT_APP_COMMIT_HASH}</span>
            )}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;
